import Axios from "axios";
import { toast } from "react-toastify";
import { getRefreshToken, clearSession } from "./auth";
import { storeDispatch, store } from "../store";
import { refreshTokens, logout } from "../store/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8082";
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || "30000", 10);

const FORCE_LOGOUT_CODES = ["ACCOUNT_DEACTIVATED", "LICENSE_INVALID", "PARTNER_DEACTIVATED", "LICENSE_EXPIRED"];

// Raw axios for refresh calls — NOT intercepted to avoid infinite loops
const rawAxios = Axios.create({
  baseURL: BACKEND_URL,
  timeout: API_TIMEOUT,
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let pendingQueue = []; // { resolve, reject }[]

function drainQueue(err, token) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve(token);
  });
  pendingQueue = [];
}

function forceLogout(message) {
  clearSession();
  storeDispatch(logout());
  toast.dismiss();
  toast.error(message);
  // PrivateRoute watches Redux isAuthenticated and navigates to /login automatically
}

const axiosInstance = Axios.create({
  baseURL: BACKEND_URL,
  timeout: API_TIMEOUT,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
});

// ─── Request interceptor ──────────────────────────────────────────────────────

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_KEY || "auth_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers.lng = localStorage.getItem("i18nextLng") || "en";
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error (no response from server)
    if (!error.response) {
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    const { status, data: errData } = error.response;
    // Backend emits `errorCode`; `code`/`error` kept as fallbacks for safety.
    const errCode = errData?.errorCode || errData?.code || errData?.error;

    // ── 401: attempt silent token refresh ──────────────────────────────────────
    if (status === 401 && originalRequest && !originalRequest._retry) {
      // Not logged in at all (e.g. wrong credentials on login page) — let caller handle
      const isAuthenticated = store.getState().auth.isAuthenticated
        || localStorage.getItem("authenticated") === "true";
      if (!isAuthenticated) {
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        forceLogout("Session expired. Please log in again.");
        return Promise.reject(error);
      }

      // Another refresh already in flight — queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await rawAxios.post("/v1/auth/refresh", { refreshToken });
        const newToken = res.data.token;
        const newRefresh = res.data.refreshToken;

        storeDispatch(refreshTokens({ token: newToken, refreshToken: newRefresh }));
        drainQueue(null, newToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        drainQueue(refreshError, null);
        const refreshStatus = refreshError?.response?.status;
        const refreshMsg = refreshError?.response?.data?.message || refreshError?.message || "Unknown error";
        console.error(`[Auth] Token refresh failed — status=${refreshStatus}, reason=${refreshMsg}`);
        forceLogout("Session expired. Please log in again.");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── 403: policy violation ──────────────────────────────────────────────────
    if (status === 403) {
      if (FORCE_LOGOUT_CODES.includes(errCode)) {
        forceLogout(errData?.message || "Access denied. Please contact your administrator.");
      } else {
        toast.error("You don't have permission to perform this action.");
      }
      return Promise.reject(error);
    }

    // ── Other errors ───────────────────────────────────────────────────────────
    const message = errData?.message || error.message;
    if (status === 503) toast.warning("Service unavailable. Please try again later.");
    else if (status === 500) toast.error("Server error. Please try again later.");
    else if (status === 404) toast.error("Resource not found.");
    // 400, 409, 422 — let the caller show the specific backend message; interceptor stays silent
    else if (status !== 401 && status !== 400 && status !== 409 && status !== 422) toast.error(message || "An error occurred. Please try again.");

    return Promise.reject(error);
  }
);

export async function post(path, body, options = {}) {
  const response = await axiosInstance.post(path, body, options);
  return response.data;
}
export async function get(path, options = {}) {
  const response = await axiosInstance.get(path, options);
  return response.data;
}
export async function put(path, body, options = {}) {
  const response = await axiosInstance.put(path, body, options);
  return response.data;
}
export async function del(path, options = {}) {
  const response = await axiosInstance.delete(path, options);
  return response.data;
}
export async function patch(path, body, options = {}) {
  const response = await axiosInstance.patch(path, body, options);
  return response.data;
}

export { axiosInstance };
// eslint-disable-next-line import/no-anonymous-default-export
export default { get, post, put, patch, del, delete: del, axiosInstance };
